
import AppLayout from '@/components/layout/AppLayout';
import { useAuth } from '@/contexts/AuthContext';
import { useActivation } from '@/contexts/ActivationContext';
import { useAdelantoCart } from '@/contexts/AdelantoCartContext';
import { useCallback, useEffect, useState } from 'react';
import { getEndpointUrl } from '@/lib/api-config';
import { httpClient } from '@/lib/http-client';
import MelBalanceWidget from '@/components/mel/MelBalanceWidget';
import { Link } from 'react-router-dom';

const formatCLP = (value: number) =>
  new Intl.NumberFormat('es-CL', {
    style: 'currency',
    currency: 'CLP',
    maximumFractionDigits: 0,
  }).format(value);

function IndicatorSkeleton() {
  return (
    <div className="space-y-2 animate-pulse">
      <div className="h-7 w-28 bg-slate-200 dark:bg-slate-700 rounded" />
      <div className="h-3 w-20 bg-slate-200 dark:bg-slate-700 rounded" />
    </div>
  );
}

export default function DashboardPage() {
  const { user } = useAuth();
  const { pendingCount, isLoading: activationLoading } = useActivation();
  const isFullyActivated = !activationLoading && pendingCount === 0;

  const [totalAdelantos, setTotalAdelantos] = useState<number | null>(null);
  const [cuotasDisponibles, setCuotasDisponibles] = useState<number | null>(null);
  const [loadingIndicators, setLoadingIndicators] = useState(false);
  const { registerOnSolicitudConfirmada } = useAdelantoCart();

  const fetchIndicators = useCallback(async () => {
    setLoadingIndicators(true);
    const results = await Promise.allSettled([
      httpClient.get<{ pagination: { total: number } }>(getEndpointUrl('adelantos', 'solicitudes')),
      httpClient.get<{ cuotasDisponibles: number }>(getEndpointUrl('dashboard', 'cuotas-disponibles')),
    ]);

    if (results[0].status === 'fulfilled') setTotalAdelantos(results[0].value.pagination.total);
    if (results[1].status === 'fulfilled') {
      setCuotasDisponibles(results[1].value.cuotasDisponibles);
    }

    setLoadingIndicators(false);
  }, []);

  useEffect(() => {
    if (!isFullyActivated) return;
    fetchIndicators();
  }, [isFullyActivated, fetchIndicators]);

  // Tras confirmar un adelanto: el contador "Mis adelantos" suma uno y las
  // cuotas disponibles bajan, así que reflejarlo sin esperar a que el
  // comercio vuelva a entrar al dashboard.
  useEffect(() => {
    if (!isFullyActivated) return;
    return registerOnSolicitudConfirmada(() => {
      fetchIndicators();
    });
  }, [isFullyActivated, registerOnSolicitudConfirmada, fetchIndicators]);

  return (
    <AppLayout>
      <div className="space-y-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Bienvenido, {user?.name}
            </h1>
            <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
              Gestiona tus cuotas y adelantos desde tu panel principal.
            </p>
          </div>
          {!activationLoading && !isFullyActivated && (
            <Link
              to="/configuraciones/resumen"
              className="inline-flex items-center gap-2 bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold shadow-md shadow-primary/20 transition-all"
            >
              <span className="material-symbols-outlined text-base">settings</span>
              Completar configuración
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {/* Card 1 — Línea de adelantos. Cuando activa: linkea a /cartola/linea
              (la cartola del MEL). Cuando pendiente: card estática "activa para ver". */}
          {isFullyActivated ? (
            <Link
              to="/cartola/linea"
              className="group block rounded-xl transition-shadow hover:shadow-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <MelBalanceWidget />
            </Link>
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                  <span className="material-symbols-outlined text-primary text-xl">account_balance_wallet</span>
                </div>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Tu línea de adelantos</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">—</p>
              <p className="text-xs text-slate-400 mt-2">Activa tu cuenta para ver tu línea de adelantos</p>
            </div>
          )}

          {/* Card 2 — Adelantos solicitados. Cuando activa: linkea a /adelantos/solicitudes. */}
          {isFullyActivated ? (
            <Link
              to="/adelantos/solicitudes"
              className="group bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm transition-all hover:shadow-md hover:border-emerald-200 dark:hover:border-emerald-900/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                  <span className="material-symbols-outlined text-emerald-600 text-xl">trending_up</span>
                </div>
                <span className="material-symbols-outlined text-slate-300 group-hover:text-emerald-600 transition-colors text-xl">chevron_right</span>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Adelantos solicitados</p>
              {loadingIndicators ? (
                <IndicatorSkeleton />
              ) : (
                <>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                    {totalAdelantos !== null ? totalAdelantos : '—'}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">Ver mis solicitudes</p>
                </>
              )}
            </Link>
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-full bg-emerald-100 dark:bg-emerald-900/20 flex items-center justify-center">
                  <span className="material-symbols-outlined text-emerald-600 text-xl">trending_up</span>
                </div>
                <span className="text-xs font-medium text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-full">
                  Este mes
                </span>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Adelantos solicitados</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">—</p>
              <p className="text-xs text-slate-400 mt-2">Sin adelantos registrados</p>
            </div>
          )}

          {/* Card 3 — Cuotas disponibles. Cuando activa: linkea a /adelantos/cuotas. */}
          {isFullyActivated ? (
            <Link
              to="/adelantos/cuotas"
              className="group bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm transition-all hover:shadow-md hover:border-blue-200 dark:hover:border-blue-900/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary"
            >
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                  <span className="material-symbols-outlined text-blue-600 text-xl">receipt_long</span>
                </div>
                <span className="material-symbols-outlined text-slate-300 group-hover:text-primary transition-colors text-xl">chevron_right</span>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Cuotas disponibles para adelantar</p>
              {loadingIndicators ? (
                <IndicatorSkeleton />
              ) : (
                <>
                  <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                    {cuotasDisponibles !== null ? cuotasDisponibles : '—'}
                  </p>
                  <p className="text-xs text-slate-400 mt-2">Solicitar un adelanto</p>
                </>
              )}
            </Link>
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-xl border border-slate-200 dark:border-slate-800 p-6 shadow-sm">
              <div className="flex items-center justify-between mb-4">
                <div className="w-10 h-10 rounded-full bg-blue-100 dark:bg-blue-900/20 flex items-center justify-center">
                  <span className="material-symbols-outlined text-blue-600 text-xl">receipt_long</span>
                </div>
                <span className="text-xs font-medium text-slate-400 bg-slate-100 dark:bg-slate-800 px-2 py-1 rounded-full">
                  Disponibles ahora
                </span>
              </div>
              <p className="text-sm text-slate-500 dark:text-slate-400">Cuotas disponibles para adelantar</p>
              <p className="text-2xl font-bold text-slate-900 dark:text-white mt-1">—</p>
              <p className="text-xs text-slate-400 mt-2">Activa tu cuenta para ver tus cuotas</p>
            </div>
          )}
        </div>

        {!activationLoading && !isFullyActivated && (
          <div className="bg-blue-50 dark:bg-blue-900/10 rounded-xl border border-blue-100 dark:border-blue-900/30 p-6">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <div className="w-12 h-12 rounded-full bg-white dark:bg-slate-800 flex items-center justify-center shadow-sm flex-shrink-0">
                <span className="material-symbols-outlined text-primary text-2xl">rocket_launch</span>
              </div>
              <div className="flex-grow">
                <h3 className="font-bold text-slate-900 dark:text-white">Activa tu cuenta para comenzar</h3>
                <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                  Completa los 6 pasos de configuración para habilitar todas las funcionalidades de adelanto de cuotas.
                </p>
              </div>
              <Link
                to="/configuraciones/resumen"
                className="flex-shrink-0 inline-flex items-center gap-2 bg-primary hover:bg-blue-600 text-white px-4 py-2 rounded-lg text-sm font-bold transition-all"
              >
                Configurar ahora
                <span className="material-symbols-outlined text-base">arrow_forward</span>
              </Link>
            </div>
          </div>
        )}
      </div>
    </AppLayout>
  );
}
